/**
 * PropertyIdCardButton — Iter-190 · wallet-friendly QR card.
 *
 * Renders a compact button on the Resident/Tenant dashboard. On click,
 * opens a modal with the server-rendered branded QR card (700×440 PNG
 * from `GET /api/properties/qr-card/{code}`), plus quick actions to
 * download or open the raw image for printing.
 */
import { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { QrCode, Download, ExternalLink, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PropertyIdCardButton({ propertyCode, propertyName }) {
  const [open, setOpen] = useState(false);
  if (!propertyCode) return null;
  const qrUrl = `${API}/api/properties/qr-card/${encodeURIComponent(propertyCode)}`;

  return (
    <>
      <Card
        className="mb-6 p-4 border-2 border-[#FFD60A] bg-gradient-to-br from-[#FFFBEA] to-white flex flex-wrap items-center justify-between gap-3"
        data-testid="property-id-card-strip"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-11 h-11 rounded-lg bg-[#FFD60A] flex items-center justify-center flex-shrink-0">
            <QrCode className="h-5 w-5 text-black" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#7a6300]">
              Your Property / Facility ID
            </div>
            <div className="font-mono text-sm sm:text-base font-bold text-zinc-900 truncate">
              {propertyCode}
            </div>
            {propertyName && (
              <div className="text-xs text-zinc-600 truncate">{propertyName}</div>
            )}
          </div>
        </div>
        <Button
          type="button"
          onClick={() => setOpen(true)}
          className="bg-black text-[#FFD60A] hover:bg-zinc-800 font-bold"
          data-testid="property-id-card-open"
        >
          <QrCode className="h-4 w-4 mr-1" /> Show my QR card
        </Button>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-2xl bg-zinc-950 border-2 border-[#FFD60A] p-0"
          data-testid="property-id-card-dialog"
        >
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-zinc-800">
            <DialogTitle className="text-white flex items-center gap-2">
              <QrCode className="h-5 w-5 text-[#FFD60A]" />
              Property / Facility Access Card
            </DialogTitle>
          </DialogHeader>
          <div className="p-6 space-y-4">
            <p className="text-xs text-zinc-400">
              On-site staff can scan this card with any camera app to look up your unit in a tap.
              Save it to your Photos or add it to your wallet for offline access.
            </p>
            <div className="rounded-lg overflow-hidden border-2 border-[#FFD60A] bg-white">
              <img
                src={qrUrl}
                alt={`NECS Property Access Card for ${propertyCode}`}
                className="w-full h-auto block"
                data-testid="property-id-card-image"
              />
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <a
                href={qrUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 px-4 py-2 text-sm font-bold rounded-md border border-zinc-700 text-white hover:bg-zinc-900"
                data-testid="property-id-card-print"
              >
                <ExternalLink className="h-4 w-4" /> Open for printing
              </a>
              <a
                href={qrUrl}
                download={`necs-${propertyCode}.png`}
                className="inline-flex items-center gap-1 px-4 py-2 text-sm font-bold rounded-md bg-[#FFD60A] text-black hover:bg-[#EAB308]"
                data-testid="property-id-card-download"
              >
                <Download className="h-4 w-4" /> Save to Photos
              </a>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                className="border-zinc-700 text-white hover:bg-zinc-900"
                data-testid="property-id-card-close"
              >
                <X className="h-4 w-4 mr-1" /> Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
